"use client";

import { useState } from "react";
import {
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  CellsResult,
  Failure,
  IamResult,
  MagnificationResult,
  ScaleBarResult,
} from "@/components/tools/biology-lab/microscope-panels";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  type IamTarget,
  type LengthUnit,
  toMicrons,
} from "@/lib/tool-kit/biology-lab/microscope";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const MICROSCOPE_MAGNIFICATION_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to work out?",
  modeMagnification: "Total magnification and field of view",
  modeIam: "Actual size, image size or magnification",
  modeScale: "Actual size from a scale bar",
  modeCells: "Cell size from cells across the field",
  objective: "Objective magnification (×)",
  eyepiece: "Eyepiece magnification (×)",
  auxiliary: "Extra lens or tube factor (×)",
  auxiliaryHint: "1 unless your microscope has an intermediate or zoom lens.",
  fieldNumber: "Eyepiece field number (mm)",
  fieldNumberHint:
    "Printed on the eyepiece, often 18 to 26.5; 22 is typical for a 10× eyepiece.",
  solveFor: "Solve for",
  solveActual: "Actual size of the specimen",
  solveImage: "Size of the image",
  solveMagnification: "Magnification",
  actualSize: "Actual size",
  imageSize: "Image size",
  magnification: "Magnification (×)",
  lengthUnit: "Unit",
  unitNm: "nm",
  unitUm: "µm",
  unitMm: "mm",
  unitCm: "cm",
  barMeasured: "Scale bar length as you measure it",
  barValue: "Length the scale bar stands for",
  featureMeasured: "Feature length, measured the same way",
  measureUnit: "Measured in",
  measureMm: "mm (on a print or screen)",
  measureCm: "cm (on a print or screen)",
  measurePx: "pixels",
  measureShortMm: "mm",
  measureShortCm: "cm",
  measureShortPx: "pixel",
  cellsAcross: "Cells lined up across the field",
  results: "Results",
  totalMagnification: "Total magnification",
  fovMm: "Field of view diameter (mm)",
  fovUm: "Field of view diameter (µm)",
  fieldArea: "Field area (mm²)",
  resultActual: "Actual size",
  resultImage: "Image size",
  resultMagnification: "Magnification",
  inMicrons: "In micrometres",
  perMeasure: "Specimen length per {unit}",
  cellLength: "Estimated cell length",
  working: "The working",
  workingTotal:
    "total = objective × eyepiece × extra lens = {obj} × {eye} × {aux} = {result}×",
  workingFov:
    "field of view = field number ÷ (objective × extra lens) = {fn} ÷ ({obj} × {aux}) = {result} mm",
  workingIam: "image size = actual size × magnification (I = A × M)",
  workingBar:
    "actual = feature ÷ bar × bar length = {feature} ÷ {bar} × {barUm} µm = {result} µm",
  workingCells:
    "cell length = field of view ÷ cells across = {fov} µm ÷ {n} = {result} µm",
  tableHeading: "Field of view with each common objective",
  colObjective: "Objective",
  colTotal: "Total magnification",
  colFov: "Field of view (mm)",
  colFovUm: "Field of view (µm)",
  copyTable: "Copy table",
  copied: "Copied!",
  errObjective: "Enter an objective magnification greater than 0.",
  errEyepiece: "Enter an eyepiece magnification greater than 0.",
  errAuxiliary: "The extra lens factor must be greater than 0.",
  errFieldNumber: "Enter a field number greater than 0.",
  errImage: "Enter an image size greater than 0.",
  errActual: "Enter an actual size greater than 0.",
  errMagnification: "Enter a magnification greater than 0.",
  errBar: "The measured scale bar length must be greater than 0.",
  errBarValue: "The scale bar's stated length must be greater than 0.",
  errFeature: "The feature length must be greater than 0.",
  errCells: "Enter a number of cells greater than 0.",
  errIam: "Enter the two known values.",
  errPositive: "The values must all be greater than 0.",
  enter: "Fill in the fields to see the result.",
} as const;

export type MicroscopeMagnificationCalculatorStrings = {
  [K in keyof typeof MICROSCOPE_MAGNIFICATION_CALCULATOR_STRINGS]: string;
};

type Mode = "magnification" | "iam" | "scale" | "cells";
type MeasureUnit = "mm" | "cm" | "px";

const ID = "mic";
const MEASURE_TO_MM: Record<MeasureUnit, number | null> = {
  mm: 1,
  cm: 10,
  px: null,
};

export default function MicroscopeMagnificationCalculatorWidget() {
  const strings = useToolUi(MICROSCOPE_MAGNIFICATION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("magnification");
  const [objective, setObjective] = useState("10");
  const [eyepiece, setEyepiece] = useState("10");
  const [auxiliary, setAuxiliary] = useState("1");
  const [fieldNumber, setFieldNumber] = useState("22");
  const [target, setTarget] = useState<IamTarget>("actual");
  const [actual, setActual] = useState("90");
  const [actualUnit, setActualUnit] = useState<LengthUnit>("um");
  const [image, setImage] = useState("36");
  const [imageUnit, setImageUnit] = useState<LengthUnit>("mm");
  const [magnification, setMagnification] = useState("400");
  const [bar, setBar] = useState("25");
  const [barLength, setBarLength] = useState("100");
  const [barUnit, setBarUnit] = useState<LengthUnit>("um");
  const [feature, setFeature] = useState("18");
  const [measureUnit, setMeasureUnit] = useState<MeasureUnit>("mm");
  const [cells, setCells] = useState("20");

  const lengthOptions = [
    { value: "nm" as const, label: strings.unitNm },
    { value: "um" as const, label: strings.unitUm },
    { value: "mm" as const, label: strings.unitMm },
    { value: "cm" as const, label: strings.unitCm },
  ];
  const measureLabels: Record<MeasureUnit, string> = {
    mm: strings.measureShortMm,
    cm: strings.measureShortCm,
    px: strings.measureShortPx,
  };

  const obj = parseMeasuredNumber(objective);
  const eye = parseMeasuredNumber(eyepiece);
  const aux = parseMeasuredNumber(auxiliary);
  const fn = parseMeasuredNumber(fieldNumber);

  function optics(needEyepiece: boolean) {
    if (obj === null || !(obj > 0)) {
      return "objective" as const;
    }
    if (needEyepiece && (eye === null || !(eye > 0))) {
      return "eyepiece" as const;
    }
    if (aux === null || !(aux > 0)) {
      return "auxiliary" as const;
    }
    return fn === null || !(fn > 0) ? ("fieldNumber" as const) : null;
  }

  function magnificationView() {
    const problem = optics(true);
    if (
      problem !== null ||
      obj === null ||
      eye === null ||
      aux === null ||
      fn === null
    ) {
      return <Failure code={problem ?? "objective"} strings={strings} />;
    }
    return (
      <MagnificationResult
        auxiliary={aux}
        eyepiece={eye}
        fieldNumber={fn}
        fmt={fmt}
        objective={obj}
        strings={strings}
      />
    );
  }

  function cellsView() {
    const n = parseMeasuredNumber(cells);
    const problem = optics(false);
    if (problem !== null || obj === null || aux === null || fn === null) {
      return <Failure code={problem ?? "objective"} strings={strings} />;
    }
    if (n === null || !(n > 0)) {
      return <Failure code="cells" strings={strings} />;
    }
    return (
      <CellsResult
        auxiliary={aux}
        cells={n}
        fieldNumber={fn}
        fmt={fmt}
        objective={obj}
        strings={strings}
      />
    );
  }

  function iamView() {
    const a = parseMeasuredNumber(actual);
    const i = parseMeasuredNumber(image);
    const m = parseMeasuredNumber(magnification);
    return (
      <IamResult
        actualUm={a === null ? null : toMicrons(a, actualUnit)}
        fmt={fmt}
        imageUm={i === null ? null : toMicrons(i, imageUnit)}
        magnification={m}
        strings={strings}
        target={target}
      />
    );
  }

  function scaleView() {
    const b = parseMeasuredNumber(bar);
    const bl = parseMeasuredNumber(barLength);
    const f = parseMeasuredNumber(feature);
    if (b === null || bl === null || f === null) {
      return <Notice>{strings.enter}</Notice>;
    }
    return (
      <ScaleBarResult
        barMeasured={b}
        barUm={toMicrons(bl, barUnit)}
        featureMeasured={f}
        fmt={fmt}
        measureLabel={measureLabels[measureUnit]}
        measureUnitMm={MEASURE_TO_MM[measureUnit]}
        strings={strings}
      />
    );
  }

  const views: Record<Mode, () => React.JSX.Element> = {
    magnification: magnificationView,
    iam: iamView,
    scale: scaleView,
    cells: cellsView,
  };
  const showOptics = mode === "magnification" || mode === "cells";

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name={`${ID}-mode`}
        onChange={setMode}
        options={[
          { value: "magnification", label: strings.modeMagnification },
          { value: "iam", label: strings.modeIam },
          { value: "scale", label: strings.modeScale },
          { value: "cells", label: strings.modeCells },
        ]}
        value={mode}
      />
      {showOptics ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <TextField
            id={`${ID}-obj`}
            label={strings.objective}
            numeric
            onChange={setObjective}
            value={objective}
          />
          {mode === "magnification" ? (
            <TextField
              id={`${ID}-eye`}
              label={strings.eyepiece}
              numeric
              onChange={setEyepiece}
              value={eyepiece}
            />
          ) : null}
          <TextField
            hint={strings.auxiliaryHint}
            id={`${ID}-aux`}
            label={strings.auxiliary}
            numeric
            onChange={setAuxiliary}
            value={auxiliary}
          />
          <TextField
            hint={strings.fieldNumberHint}
            id={`${ID}-fn`}
            label={strings.fieldNumber}
            numeric
            onChange={setFieldNumber}
            value={fieldNumber}
          />
        </div>
      ) : null}
      {mode === "cells" ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <TextField
            id={`${ID}-cells`}
            label={strings.cellsAcross}
            numeric
            onChange={setCells}
            value={cells}
          />
        </div>
      ) : null}
      {mode === "iam" ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SelectField
            id={`${ID}-target`}
            label={strings.solveFor}
            onChange={setTarget}
            options={[
              { value: "actual", label: strings.solveActual },
              { value: "image", label: strings.solveImage },
              { value: "magnification", label: strings.solveMagnification },
            ]}
            value={target}
          />
          {target === "actual" ? null : (
            <>
              <TextField
                id={`${ID}-actual`}
                label={strings.actualSize}
                numeric
                onChange={setActual}
                value={actual}
              />
              <SelectField
                id={`${ID}-actual-unit`}
                label={strings.lengthUnit}
                onChange={setActualUnit}
                options={lengthOptions}
                value={actualUnit}
              />
            </>
          )}
          {target === "image" ? null : (
            <>
              <TextField
                id={`${ID}-image`}
                label={strings.imageSize}
                numeric
                onChange={setImage}
                value={image}
              />
              <SelectField
                id={`${ID}-image-unit`}
                label={strings.lengthUnit}
                onChange={setImageUnit}
                options={lengthOptions}
                value={imageUnit}
              />
            </>
          )}
          {target === "magnification" ? null : (
            <TextField
              id={`${ID}-mag`}
              label={strings.magnification}
              numeric
              onChange={setMagnification}
              value={magnification}
            />
          )}
        </div>
      ) : null}
      {mode === "scale" ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <TextField
            id={`${ID}-bar`}
            label={strings.barMeasured}
            numeric
            onChange={setBar}
            value={bar}
          />
          <TextField
            id={`${ID}-bar-length`}
            label={strings.barValue}
            numeric
            onChange={setBarLength}
            value={barLength}
          />
          <SelectField
            id={`${ID}-bar-unit`}
            label={strings.lengthUnit}
            onChange={setBarUnit}
            options={lengthOptions}
            value={barUnit}
          />
          <TextField
            id={`${ID}-feature`}
            label={strings.featureMeasured}
            numeric
            onChange={setFeature}
            value={feature}
          />
          <SelectField
            id={`${ID}-measure-unit`}
            label={strings.measureUnit}
            onChange={setMeasureUnit}
            options={[
              { value: "mm", label: strings.measureMm },
              { value: "cm", label: strings.measureCm },
              { value: "px", label: strings.measurePx },
            ]}
            value={measureUnit}
          />
        </div>
      ) : null}
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <div className={`${RESULT_CARD} space-y-4`}>{views[mode]()}</div>
      </section>
    </>
  );
}
